import { DeviceObservationScheduleRequestInfo } from '@csp/csp-common-devices';
import { ScheduleRef } from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import { uniqWith } from 'lodash';
import { ScheduleRequestMetricCriterion } from '../../model/ScheduleRequestMetricCriterion';
import { ScheduleRequestComplianceRef } from '../../model/ScheduleRequestComplianceRef';

const toMetricCriteriaFromScheduleRequestInfos = (
  deviceObservationRequests: DeviceObservationScheduleRequestInfo[],
  daysPerMonth: number,
): ScheduleRequestMetricCriterion[] =>
  deviceObservationRequests.map(scheduleRequestInfo =>
    ScheduleRequestMetricCriterion.from({
      recentPeriod: scheduleRequestInfo.request.historicalMetricPeriod,
      scheduleRef: scheduleRequestInfo.ref,
      minComplianceThreshold: scheduleRequestInfo.request.minComplianceThreshold,
      daysPerMonth,
    }),
  );

// Multiple request compliance refs could potentially be mapped to the same schedule request info.
// This happens when we are grouping on schedule and not on schedule request, which is needed
// to create more complex schedule requests.
const toMetricCriteriaFromRequestComplianceRefs = (
  deviceObservationRequests: DeviceObservationScheduleRequestInfo[],
  requestComplianceRefs: ScheduleRequestComplianceRef[],
  daysPerMonth: number,
): ScheduleRequestMetricCriterion[] => {
  const metricCriteriaWithDuplicates = requestComplianceRefs
    .map(complianceRef => {
      const matchingRequestInfo = deviceObservationRequests.find(requestInfo =>
        ScheduleRef.matchesRequestRef(requestInfo.ref, complianceRef.requestRef),
      );
      if (!matchingRequestInfo) {
        console.error(`No matching request info for request compliance ref: ${JSON.stringify(complianceRef, null, 2)}`);
        return;
      }
      return ScheduleRequestMetricCriterion.from({
        recentPeriod: matchingRequestInfo.request.historicalMetricPeriod,
        scheduleRef: matchingRequestInfo.ref,
        minComplianceThreshold: complianceRef.minComplianceThreshold,
        daysPerMonth,
      });
    })
    .filter(isDefined);

  return uniqWith(metricCriteriaWithDuplicates, (criterion1, criterion2) =>
    ScheduleRef.isEqual(criterion1.scheduleRef, criterion2.scheduleRef),
  );
};

export const DeviceMetricCriteriaMapper = {
  toMetricCriteriaFromScheduleRequestInfos,
  toMetricCriteriaFromRequestComplianceRefs,
};
